import { type RefObject, useLayoutEffect, useRef } from 'react';

const OPTION_SELECTOR = '[role="option"]:not([aria-hidden="true"])';

interface UseVisibleRowsCapOptions {
  /** The scrollable option list (`<ListBox>`) whose height should be capped. */
  listRef: RefObject<HTMLElement | null>;
  /** Only measure while the popover is mounted and laid out. */
  isOpen: boolean;
  /** How many options must be fully visible before the list starts scrolling. */
  visibleRows: number;
  /**
   * Virtualized lists absolutely position and recycle rows, so DOM measurement is
   * meaningless there. Those lists fall back to the arithmetic cap in CSS, which is
   * why virtualization requires a uniform row height.
   */
  isVirtualized: boolean;
}

/**
 * Caps a listbox at exactly `visibleRows` options, whatever each option's height is.
 *
 * CSS cannot express this: there is no way to sum the heights of the first N children
 * (`anchor-size()` returns a size, not an offset; `calc-size()` only resolves the
 * element's own intrinsic size). So we measure and publish the result as the
 * `--select-max-height` custom property, which the stylesheet consumes as the
 * `max-height` value (with the fixed-row-height calc as its fallback).
 *
 * The cap is measured from the list's content-box top to the top of the *first hidden*
 * row. Anchoring on the content box rather than on the first option keeps a leading
 * group header inside the cap, and measuring to the next row's top absorbs dividers,
 * group headers and inter-row margins without any arithmetic about their sizes.
 */
export function useVisibleRowsCap({
  listRef,
  isOpen,
  visibleRows,
  isVirtualized,
}: UseVisibleRowsCapOptions): void {
  const appliedCap = useRef<string | null>(null);

  // No dependency array on purpose: the option set changes on every keystroke while
  // filtering, and on every async search result. Re-measuring each render is cheap and
  // the equality guard below makes it a no-op when nothing moved.
  useLayoutEffect(() => {
    const list = listRef.current;

    if (!list || !isOpen || isVirtualized) return;

    // Guard against writing the same value repeatedly, which would otherwise let a row
    // whose height depends on the list height oscillate forever.
    const applyCap = (cap: string): void => {
      if (cap === appliedCap.current) return;
      appliedCap.current = cap;
      list.style.setProperty('--select-max-height', cap);
    };

    const measure = (): void => {
      const rows = list.querySelectorAll<HTMLElement>(OPTION_SELECTOR);
      const firstHiddenRow = rows[visibleRows];

      // Fewer rows than the cap: publish `none` rather than removing the property, because
      // removing it would fall back to the arithmetic calc, which assumes a fixed row height
      // and would clip taller rows. The popover's own runtime max-height plus `min-height: 0`
      // still keep the list on screen.
      if (!firstHiddenRow) {
        applyCap('none');
        return;
      }

      // Measure from the unscrolled position so the rects are stable and a leading group
      // header is accounted for.
      const previousScrollTop = list.scrollTop;
      if (previousScrollTop !== 0) list.scrollTop = 0;

      const listRect = list.getBoundingClientRect();
      const computedStyle = getComputedStyle(list);
      const contentTop = listRect.top + parseFloat(computedStyle.borderTopWidth);
      const paddingBottom = parseFloat(computedStyle.paddingBottom);
      const firstHiddenRowTop = firstHiddenRow.getBoundingClientRect().top;

      // `max-height` is border-box (global `box-sizing: border-box`), so the list's own
      // vertical padding has to be included or the last visible row is clipped.
      const cap = firstHiddenRowTop - contentTop + paddingBottom;

      if (previousScrollTop !== 0) list.scrollTop = previousScrollTop;

      if (cap <= 0) return;

      applyCap(`${Math.ceil(cap)}px`);
    };

    measure();

    // Observe the rows rather than the list: a row's height never depends on the list's
    // max-height, so publishing the cap cannot re-trigger the observer. This also covers
    // late reflows such as web fonts finishing loading.
    const observer = new ResizeObserver(measure);
    const observedRows = Array.from(list.querySelectorAll<HTMLElement>(OPTION_SELECTOR)).slice(
      0,
      visibleRows + 1
    );

    for (const row of observedRows) {
      observer.observe(row);
    }

    return () => {
      observer.disconnect();
      // The popover unmounts on close, so the next open measures a fresh element that carries
      // no inline property. Forget the last value, or an identical cap would be skipped by the
      // equality guard and the list would fall back to the arithmetic calc.
      appliedCap.current = null;
    };
  });
}
